import MarkdownIt from 'markdown-it';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled, { keyframes } from 'styled-components';

import { useFeature } from '@treams/treams-hooks/useFeature';
import { useLocalize } from '@treams/treams-hooks/useLocalize';
import { useStreamAgent } from '@treams/treams-hooks/useStreamAgent';
import * as graphql from '@treams/treams-network';
import { AICard } from '@treams/treams-ui/common/AICard';
import { FlexGap } from '@treams/treams-ui/common/FlexGap';
import { MarkdownStyled } from '@treams/treams-ui/common/MarkdownStyled';
import { Skeleton } from '@treams/treams-ui/common/Skeleton';
import { Paragraph } from '@treams/treams-ui/common/Text';
import { tokens } from '@treams/treams-ui/theme/tokens';

import { useSmoothedText } from './smoothedText';
import { insertStreamCursor } from './streamCursor';
import { talentInsightsCacheKey, talentInsightsStreamParams } from './talentInsightsStream';

const markdown = new MarkdownIt({ breaks: true, linkify: true });

const cursorBlink = keyframes`
	0%, 49% { opacity: 1; }
	50%, 100% { opacity: 0; }
`;

const StreamMarkdown = styled(MarkdownStyled)`
	[data-stream-cursor] {
		display: inline-block;
		width: 1.5px;
		height: 0.85em;
		margin-left: 2px;
		vertical-align: -0.08em;
		background: currentColor;
		animation: ${cursorBlink} 0.9s step-end infinite;
	}
`;

const InsightsLoading = () => {
	const { t } = useTranslation();

	return (
		<FlexGap
			flexDirection="column"
			gap={tokens.gap.element}
			role="status"
			aria-label={t('talentReviews.v2.header.insightsLoading')}
		>
			<Skeleton.Box tint="purple" height={12} width="100%" />
			<Skeleton.Box tint="purple" height={12} width="92%" />
			<Skeleton.Box tint="purple" height={12} width="78%" />
		</FlexGap>
	);
};

export default function TalentInsightsCard(props: {
	user: Pick<graphql.User, 'id' | 'firstName' | 'lastName'>;
	templateID: string;
	groupID: string;
	localDate: string;
	row?: number;
	column?: number;
}) {
	const { t } = useTranslation();
	const { language } = useLocalize();
	const hasAI = useFeature(graphql.FeatureName.Ai);
	const { stream, text } = useStreamAgent();
	const [openedUserID, setOpenedUserID] = useState<string | null>(null);
	const [streaming, setStreaming] = useState(false);
	const [failed, setFailed] = useState(false);
	const [cache, setCache] = useState<Record<string, string>>({});

	const open = openedUserID === props.user.id;
	const cacheKey = talentInsightsCacheKey({
		userID: props.user.id,
		templateID: props.templateID,
		groupID: props.groupID,
		localDate: props.localDate,
		row: props.row,
		column: props.column,
	});
	const cached = Object.hasOwn(cache, cacheKey);
	const cachedText = cached ? cache[cacheKey] : undefined;
	const rawText = cachedText ?? (open ? text : '');
	const displayText = useSmoothedText(rawText, streaming && !cached);
	const dripping = Boolean(rawText) && displayText !== rawText;
	const displayHtml = useMemo(() => {
		const html = displayText ? markdown.render(displayText) : '';
		return dripping && html ? insertStreamCursor(html) : html;
	}, [displayText, dripping]);
	const loading = open && streaming && !displayText;

	useEffect(() => {
		if (!hasAI || !open || cached) {
			return;
		}

		const controller = new AbortController();
		setStreaming(true);
		setFailed(false);

		stream(
			'TALENT_INSIGHTS',
			talentInsightsStreamParams({
				userID: props.user.id,
				templateID: props.templateID,
				groupID: props.groupID,
				localDate: props.localDate,
				locale: language.id,
				row: props.row,
				column: props.column,
			}),
			controller.signal,
		)
			.then((accumulated) => {
				if (controller.signal.aborted) {
					return;
				}

				setCache((current) => ({ ...current, [cacheKey]: accumulated ?? '' }));
			})
			.catch((error) => {
				if (error instanceof DOMException && error.name === 'AbortError') {
					return;
				}

				if (!controller.signal.aborted) {
					setFailed(true);
				}
			})
			.finally(() => {
				if (!controller.signal.aborted) {
					setStreaming(false);
				}
			});

		return () => controller.abort();
		// stream identity is stable per workspace; listing it retriggers on parent renders.
	}, [
		hasAI,
		open,
		cached,
		cacheKey,
		props.user.id,
		props.templateID,
		props.groupID,
		props.localDate,
		props.row,
		props.column,
		language.id,
	]);

	if (!hasAI) {
		return null;
	}

	return (
		<AICard
			title={t('talentReviews.v2.header.insights', {
				name: `${props.user.firstName} ${props.user.lastName}`,
			})}
			count={displayText ? 1 : undefined}
			collapsed={!open}
			onHeaderClick={() =>
				setOpenedUserID((current) => (current === props.user.id ? null : props.user.id))
			}
		>
			{loading && <InsightsLoading />}
			{displayHtml && (
				<span aria-live="polite">
					<StreamMarkdown dangerouslySetInnerHTML={{ __html: displayHtml }} />
				</span>
			)}
			{open && !loading && !displayText && (
				<Paragraph>
					{t(
						failed
							? 'talentReviews.v2.header.insightsError'
							: 'talentReviews.v2.header.insightsEmpty',
					)}
				</Paragraph>
			)}
		</AICard>
	);
}
